import React, { useMemo } from "react";

/**
 * How every reveal ends: coins thrown out from the middle, confetti over
 * the lot, and the winning yantra held up with its name.
 *
 * All six animations share this so a round ends the same way whichever
 * show it put on, and so the party is written once.
 */

const COIN_GLYPHS = ["🪙", "💰", "✨", "⭐", "👑"];
const COIN_COUNT = 34;
const CONFETTI_COUNT = 90;

const random = (min, max) => min + Math.random() * (max - min);

export function Coins({ seed }) {
  const coins = useMemo(
    () =>
      Array.from({ length: COIN_COUNT }, (_, i) => ({
        id: `${seed}-${i}`,
        glyph: COIN_GLYPHS[i % COIN_GLYPHS.length],
        x: random(-330, 330),
        y: random(-240, 240),
        r: random(-600, 600),
        delay: random(0, 0.3),
      })),
    [seed],
  );

  return (
    <div className="rv-coins" aria-hidden="true">
      {coins.map((c) => (
        <span
          key={c.id}
          style={{
            "--rv-x": `${c.x}px`,
            "--rv-y": `${c.y}px`,
            "--rv-r": `${c.r}deg`,
            animationDelay: `${c.delay}s`,
          }}
        >
          {c.glyph}
        </span>
      ))}
    </div>
  );
}

export function Confetti({ seed }) {
  const bits = useMemo(
    () =>
      Array.from({ length: CONFETTI_COUNT }, (_, i) => ({
        id: `${seed}-${i}`,
        hue: Math.round(random(0, 360)),
        x: random(-430, 430),
        y: random(240, 700),
        delay: random(0, 0.35),
        duration: random(1.5, 2.6),
        width: random(5, 10),
        height: random(9, 16),
      })),
    [seed],
  );

  return (
    <div className="rv-confetti" aria-hidden="true">
      {bits.map((b) => (
        <i
          key={b.id}
          style={{
            background: `hsl(${b.hue}, 90%, 60%)`,
            width: b.width,
            height: b.height,
            animationDelay: `${b.delay}s`,
            animationDuration: `${b.duration}s`,
            "--rv-x": `${b.x}px`,
            "--rv-y": `${b.y}px`,
          }}
        />
      ))}
    </div>
  );
}

/**
 * The winner held up in the middle. `compact` is for the animations that
 * already end with the winning card centre stage and only want the name.
 */
export default function Celebration({ tile, seed, compact = false }) {
  if (!tile) return null;

  return (
    <>
      <div className="rv-flash" aria-hidden="true" />
      <Coins seed={seed} />
      <Confetti seed={seed} />
      {compact ? null : (
        <div className="rv-winner-card">
          <div className="rv-winner-label">Winner</div>
          <img className="rv-winner-img" src={tile.img} alt={tile.name || "winner"} />
          <div className="rv-winner-name">{tile.name}</div>
        </div>
      )}
    </>
  );
}
